"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Login, LoginSchema } from "@/lib/validations";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { useEffect } from "react";
import { LoginFormProps } from "@/components/auth/types";
import { LoadingButton } from "@/components/ui/loading-button";

export function LoginForm({
  onSubmit,
  status,
  onViewChange,
  defaultValues,
  onEmailChange,
  onPasswordChange,
  isLocked = false,
  lockoutError,
  lockoutSeconds = 0,
}: LoginFormProps) {
  const form = useForm<Login>({
    resolver: zodResolver(LoginSchema),
    defaultValues: defaultValues || {
      email: "",
      password: "",
    },
  });

  const { watch } = form;

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "email") onEmailChange?.(value.email as string);
      if (name === "password") onPasswordChange?.(value.password as string);
    });
    return () => subscription.unsubscribe();
  }, [watch, onEmailChange, onPasswordChange]);

  const handleSubmit = async (values: Login) => {
    try {
      // Convert to plain object before passing to server action
      const plainValues = {
        email: values.email,
        password: values.password,
      };
      const result = await onSubmit(plainValues);

      if (result.error) {
        form.setError("root", { message: result.error });
      }
    } catch (error) {
      form.setError("root", {
        message:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred",
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-4'>
        <FormField
          control={form.control}
          name='email'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder='Enter your email'
                  type='email'
                  disabled={isLocked}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='password'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Password</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder='Enter your password'
                  type='password'
                  variant='password'
                  disabled={isLocked}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {(lockoutError || form.formState.errors.root) && (
          <div
            className={cn(
              "text-sm",
              isLocked
                ? "text-red-600 flex items-center gap-2"
                : "text-destructive"
            )}
          >
            {isLocked && (
              <span className='w-4 h-4 rounded-full bg-red-600 text-white flex items-center justify-center text-xs'>
                ✗
              </span>
            )}
            {lockoutError || form.formState.errors.root?.message}
            {isLocked && lockoutSeconds > 0 && (
              <span className='ml-auto'>({lockoutSeconds}s)</span>
            )}
          </div>
        )}
        <LoadingButton
          type='submit'
          variant={isLocked ? "destructive" : "default"}
          className={cn(
            "w-full",
            status === "executing" ? "animate-pulse" : ""
          )}
          loading={status === "executing"}
          locked={isLocked}
          loadingText='Checking...'
          lockedText={
            lockoutSeconds > 0 ? `Locked (${lockoutSeconds}s)` : "Locked"
          }
          disabled={status === "executing" || isLocked}
        >
          Login
        </LoadingButton>
      </form>
      <div className='flex justify-end mt-4'>
        <Button
          variant='link'
          className='p-0 h-auto font-normal'
          onClick={() => onViewChange?.("forgotPassword")}
          disabled={isLocked}
        >
          Forgot password?
        </Button>
      </div>
    </Form>
  );
}
